import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AreaDisplayInfo } from "../../../Types/AreaDisplayInfo/AreaDisplayInfo"
import { SetArchivedEvaluateAreaRequest } from "../../../Types/Evaluate/SetArchivedEvaluateAreaRequest";
import { axiosInstance } from "../../../Authentication/AxiosInterceptor";
import { AnalyseDataRequest } from "../../../Types/Paging/PagedRequest";
import { PagedResponse } from "../../../Types/Paging/PagedResponse";
import { toast } from "react-toastify";
import { RootState } from "../Store";
import { getClient } from "./client";
import { GetPagedDevelopAreaDisplayInfosByClientAndUser as GetPagedDevelopAreaDisplayInfosByClientAndUserAPI } from "../../../API/Develop/Develop";
import { dedupeAsync } from "../../../Utils/dedupeAsync";

interface AreaState {
    areas: AreaDisplayInfo[],
    totalCount: number
}

const initialState: AreaState  = {
    areas: [],
    totalCount: 0
}

// Thunk to handle API call and update the state
export const SetArchivedEvaluateAreaById = createAsyncThunk(
    "area/setArchivedEvaluateAreaById",
    async (body: SetArchivedEvaluateAreaRequest, thunkAPI) => {
      try {
        await axiosInstance.post(`SetArchivedEvaluateAreaById`, body);
        return body; // Return the payload to update the state
      } catch (error) {
        console.error(error);
        return thunkAPI.rejectWithValue(error);
      }
    }
  );

  // Thunk to fetch paged area display info
export const GetPagedEvaluateAreaDisplayInfosByClientAndUser = createAsyncThunk(
    "area/getPagedEvaluateAreaDisplayInfosByClientAndUser",
    async (request : AnalyseDataRequest, thunkAPI) => {
      try {
        const state = thunkAPI.getState() as RootState;
        const client = getClient(state);

        console.log("Fetching areas for client:", client?.id, "with request:", request);

        return dedupeAsync(
          `eval-areas:${client?.id}:${request.archived}:${request.pageNumber}:${request.searchTerm ?? ""}`,
          async () => {
            const response = await axiosInstance.put(
              `GetPagedEvaluateAreaDisplayInfosByClientAndUser/${client?.id}/`,
              request
            );
            return response.data as PagedResponse<AreaDisplayInfo>;
          }
        );
      } catch (error) {
        console.error(error);
        toast.error(`Unable to load areas. Please try again later.`)
        return thunkAPI.rejectWithValue(error);
      }
    }
  );

  // Thunk to fetch paged Develop area display info
export const GetPagedDevelopAreaDisplayInfosByClientAndUser = createAsyncThunk(
    "area/getPagedDevelopAreaDisplayInfosByClientAndUser",
    async (request : AnalyseDataRequest, thunkAPI) => {
      try {
        const state = thunkAPI.getState() as RootState;
        const client = getClient(state);

        console.log("Fetching Develop areas for client:", client?.id, "with request:", request);

        return dedupeAsync(
          `develop-areas:${client?.id}:${request.archived}:${request.pageNumber}:${request.searchTerm ?? ""}`,
          () => GetPagedDevelopAreaDisplayInfosByClientAndUserAPI(client?.id || "", request)
        );
      } catch (error) {
        console.error(error);
        toast.error(`Unable to load areas. Please try again later.`)
        return thunkAPI.rejectWithValue(error);
      }
    }
  );
  
  export const AreaSlice = createSlice({
    name: "Area",
    initialState,
    reducers: {
      addArea: (state, action: PayloadAction<{ name: string }>) => {
        console.log("Area added to state.");
      },
    },
    extraReducers: (builder) => {
        builder.addCase(SetArchivedEvaluateAreaById.fulfilled, (state, action) => {
            const { areaId, archived } = action.payload;
          
              state.areas = state.areas.filter((area) => area.id !== areaId);
              // Otherwise, just update the `archived` status
              const areaIndex = state.areas.findIndex((area) => area.id === areaId);
              if (areaIndex !== -1) {
                state.areas[areaIndex].archived = archived;
              }
            
          });
          
  
      builder.addCase(SetArchivedEvaluateAreaById.rejected, (state, action) => {
        console.error("Failed to set archived status:", action.payload);
      });

      builder.addCase(GetPagedEvaluateAreaDisplayInfosByClientAndUser.fulfilled, (state, action) => {
        const { items, totalCount } = action.payload;
  
        // Update the areas and total count in the state
        state.areas = items;
        state.totalCount = totalCount;
      });
  
      builder.addCase(GetPagedEvaluateAreaDisplayInfosByClientAndUser.rejected, (state, action) => {
        console.error("Failed to fetch area display info:", action.payload);
      });

      builder.addCase(GetPagedDevelopAreaDisplayInfosByClientAndUser.fulfilled, (state, action) => {
        const { items, totalCount } = action.payload;
  
        // Update the areas and total count in the state
        state.areas = items;
        state.totalCount = totalCount;
      });
  
      builder.addCase(GetPagedDevelopAreaDisplayInfosByClientAndUser.rejected, (state, action) => {
        console.error("Failed to fetch Develop area display info:", action.payload);
      });

      // Archived fetching now uses GetPagedEvaluateAreaDisplayInfosByClientAndUser with archived flag
    },
  });
  
  export default AreaSlice.reducer;
  export const { addArea } = AreaSlice.actions;